import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { path } from 'ramda'
import { NextSeo } from 'next-seo'
import { DateTime } from 'luxon'
import Container from '@material-ui/core/Container'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import ReactMarkdown from 'react-markdown'
import stripMarkdown from '@lib/strip-markdown'
import Chip from '@material-ui/core/Chip'
import CheckIcon from '@material-ui/icons/Check'
import LinkIcon from '@material-ui/icons/Link'
import Link from '@components/link'
import Divider from '@material-ui/core/Divider'
import Card from '@material-ui/core/Card'
import Hidden from '@material-ui/core/Hidden'
import NoSsr from '@material-ui/core/NoSsr'
import EventNoteIcon from '@material-ui/icons/EventNote'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import CreditCardIcon from '@material-ui/icons/CreditCard'
import TodayIcon from '@material-ui/icons/Today'
import LockIcon from '@material-ui/icons/Lock'
import CardContent from '@material-ui/core/CardContent'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'

import {
  getDate,
  getTime,
  fullDayDateAndTimeWithTimezone,
  getShortDate,
  getTimeWithTimeZone,
} from '@utils/dateUtils'
import defaults from '@config/defaults'
import { JsonLd } from '@components/atoms'
import SessionsAccordion from '@components/group-event-card/sessions-accordion'
import { centsToDollars, centsToDollarsFormatted } from '@lib/conversions'
import numberToWords from '@utils/numberToWords'
import pluralize from '@lib/pluralize'
import {
  isWeeklyRecurringPayment, isMonthlyRecurringPayment, formattedGroupEventPrice, isRecurringPayment,
} from '@utils/groupEventUtils'
import { useSendImpressionEvent } from '@lib/gtm-utils'
import EventMap from './event-map'
import MoreEvents from './more-events'
import RegisterButton from './register-button'
import { useStyles } from './styles'
import EventHostInfo from './event-host-info'

const buildHeroImageUrl = (imageUrl, isMobile) => {
  const url = new URL(imageUrl)

  if (imageUrl.includes('unsplash')) {
    return `${url.origin}${url.pathname}?ixid=${url.searchParams.get('ixid')}&w=${isMobile ? 600 : 1920}`
  }
  if (imageUrl.includes('ctfassets')) {
    url.searchParams.set('w', isMobile ? 600 : 1920)
    return url.href
  }
  // custom uploaded images
  if (imageUrl.includes('res.cloudinary.com/pgahq/image/upload')) {
    const [part1, part2] = imageUrl.split('upload')
    return `${part1}upload/w_${isMobile ? 600 : 1920}${part2}`
  }

  return imageUrl
}

const buildPaymentDurationLabel = (groupEvent) => {
  if (isWeeklyRecurringPayment(groupEvent)) {
    return `Billed weekly for ${numberToWords(groupEvent.numberOfPayments)} ${pluralize(groupEvent.numberOfPayments, 'week')}`
  } if (isMonthlyRecurringPayment(groupEvent)) {
    return `Billed monthly for ${numberToWords(groupEvent.numberOfPayments)} ${pluralize(groupEvent.numberOfPayments, 'month')}`
  }
  return ''
}

const GroupEventPage = (props) => {
  const { event } = props
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [sessionsExpanded, setSessionsExpanded] = useState(false)

  const showWaitlistDialog = event.registrationFull && event.hasWaitlist
  const waitlistText = event.maxAttendance === 0 ? 'Waitlist Open' : 'Registration Full'

  const priceInDollars = centsToDollars(event.priceInCents)

  const totalPriceInDollars = event.totalPriceInCents
    ? centsToDollars(event.totalPriceInCents)
    : null
  const totalPriceInDollarsFormatted = event.totalPriceInCents
    ? centsToDollarsFormatted(event.totalPriceInCents)
    : null

  const eventURL = `${process.env.PGA_COM_URL}/things-to-do/events/${event.slug}`
  const profileUrl = path(['coach', 'profileUrl'], event)

  const academySlug = path(['academy', 'slug'], event) || null
  const isAcademyEvent = academySlug?.startsWith('pga-hope-')
  const isFCCEvent = academySlug === 'pga-coaching-center-at-pga-frisco'
  const pgaCampHeroImage = 'https://images.ctfassets.net/56u5qdsjym8c/5cBZukRsEHDlYWJns1D9yQ/c75e3a28fdcee692462d3d3f24016e24/pga-junior-camp-kids.jpg'
  const isPGACampEvent = event.slug?.startsWith('pga-camps-')

  const academyUrl = () => {
    if (isAcademyEvent) {
      return `${process.env.PGA_COM_URL}/pga-hope/${academySlug.replace('pga-hope-', '')}`
    }
    if (isFCCEvent) {
      return `${process.env.PGA_COM_URL}/pga-coaching-center-at-pga-frisco`
    }
    return `${process.env.PGA_COM_URL}/coaching-centers/${academySlug}`
  }

  const academyTitle = (() => {
    if (isAcademyEvent) {
      return 'Section'
    } if (isFCCEvent) {
      return 'Coaching Center'
    }
    return 'Hosted by'
  })()

  const handleSessionsClick = () => {
    setSessionsExpanded(true)
    const headerOffset = 90
    const scrollToElement = document.getElementById('event-details')
    const desiredPosition = scrollToElement.getBoundingClientRect().top
      + window.scrollY - headerOffset
    window.scrollTo({ top: desiredPosition, behavior: 'smooth' })
  }

  const seoTitle = `${event.title} - ${path(['coachFacility', 'name'], event)} - ${path(['coachFacility', 'city'], event)}, ${path(['coachFacility', 'state'], event)} | PGA.com | Golf Activities Near Me | Golf courses near me | Golf Events | Golf Leagues`
  const truncate = (str, max, suffix) => (str.length < max ? str : `${str.substr(0, str.substr(0, max - suffix.length).lastIndexOf(' '))}${suffix}`)
  const description = truncate(stripMarkdown(event.description || ''), 200, '...')
  const heroImage = isPGACampEvent ? pgaCampHeroImage : event.featuredImageUrl

  const defaultKeywords = `${event.tags.join(', ')}, PGA of America, ${event.title}, ${path(['coach', 'name'], event)},
  ${path(['coachFacility', 'city'], event)}, ${path(['coachFacility', 'state'], event)}, ${path(['coachFacility', 'name'], event)}`

  const genericGolfKeywords = 'Youth Golf, Girls Golf, Boys Golf, Teen Golf, Golf Events, Tournaments, Charity Events, Club Fittings, Clinics, Workshops, Classes, Camps, Schools, Programs, Training, Practice, Activity, Leagues, Family Golf'
  const pgaCampKeywords = 'PGA Camps, Kids Golf Camps, Summer Golf Camp'

  const keyWords = `${defaultKeywords}, ${genericGolfKeywords}, ${isPGACampEvent ? pgaCampKeywords : ''}`

  const start = DateTime.fromISO(event.startDateTime)
  const end = DateTime.fromISO(event.endDateTime)
  const duration = end.diff(start)
  const eventDuration = duration.toISO()

  const noIndex = event.unlisted
    || event.isCanceled
    || event.registrationCloseDateTime < DateTime.now().toISO()

  const RequestAccessCode = () => (
    <Typography className={classes.cardText} gutterBottom>
      Requires Access Code
    </Typography>
  )

  useSendImpressionEvent({
    event: 'view-event',
    attributes: {
      title: event.title,
      category: event.tags,
      start_date: DateTime.fromISO(event.startDateTime).toFormat('yyyy-LL-dd'),
      start_time: DateTime.fromISO(event.startDateTime).toFormat('hh:mm a'),
      facility: path(['coachFacility', 'name'], event),
      price: event.priceInCents / 100,
      coach_id: path(['coach', 'id'], event),
      coach_name: path(['coach', 'name'], event),
    },
  })

  useEffect(() => {
    const fromQRCode = window.location.search.includes('from-qr-code=true')
    if (fromQRCode) {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: `scanned-qrCode-group-event-${event.slug}`,
        source: 'my-pga-coach',
        medium: 'qr-code-scan',
        campaign: 'my-pga-coach-qr-code-scans',
      })
    } else {
      window.dataLayer = window.dataLayer || []
      window.dataLayer.push({
        event: `clicked-group-event-${event.slug}`,
        source: 'user-initiated-click',
        medium: 'group-event',
        campaign: 'my-pga-coach-qr-code-scans',
      })
    }
  }, [])

  return (
    <>
      <NextSeo
        noindex={noIndex}
        nofollow={defaults.noindexNofollow}
        title={seoTitle}
        description={description}
        canonical={eventURL}
        openGraph={{
          type: 'website',
          title: seoTitle,
          description: event.description && stripMarkdown(event.description),
          url: eventURL,
          images: event.featuredImageUrl && [{ url: event.featuredImageUrl }],
        }}
        keywords={keyWords}
      />
      <JsonLd {...{
        '@type': 'BreadcrumbList',
        itemListElement: [{
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': 'https://www.pga.com/things-to-do',
            name: 'Golf activities near me',
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@id': 'https://www.pga.com/things-to-do/events',
            name: 'Golf events',
          },
        },
        {
          '@type': 'ListItem',
          position: 3,
          item: {
            '@id': eventURL,
            name: event.title,
          },
        },
        ],
      }}
      />

      {/* https://schema.org/Event */}
      <JsonLd {...{
        offers: {
          '@type': 'Offer',
          url: `${eventURL}`,
          price: Number(priceInDollars),
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          validFrom: event.createdAt,
          validThrough: event.registrationCloseDateTime,
          availabilityEnds: event.registrationCloseDateTime,
          itemOffered: {
            '@type': 'Event',
            name: event.title,
            description: event.description,
            startDate: event.startDateTime,
            endDate: event.endDateTime,
            duration: eventDuration,
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            eventStatus: event.isCanceled ? 'https://schema.org/EventCancelled' : 'https://schema.org/EventScheduled',
            image: event.featuredImageUrl || 'https://www.pga.com/images/Private.jpg',
            location: {
              '@type': 'Place',
              name: event.coachFacility.name,
              address: {
                '@type': 'PostalAddress',
                streetAddress: event.coachFacility.street,
                addressLocality: event.coachFacility.city,
                postalCode: event.coachFacility.zipCode,
                addressRegion: event.coachFacility.state,
                addressCountry: 'US',
              },
            },
            eventSchedule: {
              '@type': 'Schedule',
              startDate: event.startDateTime,
              endDate: event.endDateTime,
              byDay: event.sessions?.map(
                session => DateTime.fromISO(session.startDateTime).weekdayLong,
              ),
            },
            organizer: {
              '@type': 'Person',
              name: path(['coach', 'name'], event),
              url: profileUrl,
            },
            performer: {
              '@type': 'Person',
              name: path(['coach', 'name'], event),
            },
          },
        },
        organizer: {
          '@type': 'Person',
          name: path(['coach', 'name'], event),
          url: profileUrl,
        },
        performer: {
          '@type': 'Person',
          name: path(['coach', 'name'], event),
        },
        keywords: keyWords,
      }}
      />
      <img src={buildHeroImageUrl(heroImage, isMobile)} alt="header" className={isPGACampEvent ? classes.campFeaturedImg : classes.featuredImg} />
      <Container className={classes.root}>
        <Box display="flex" marginTop={2}>
          <Box display="flex" flexDirection="column" flexGrow={1} mr={2}>
            <Typography variant="h1" className={classes.title}>{event.title}</Typography>
            <Box mb={2} className={classes.descriptionWrapper}>
              {/* eslint-disable react/prop-types */}
              <ReactMarkdown
                skipHtml
                components={{
                  p: ({ node: { children }, ordered, ...rest }) => {
                    // Check for single-line bold markdown **example**
                    const isSingleLineBold = (children && children.findIndex(c => c.tagName === 'strong') >= 0)

                    return (
                      <Typography
                        {...rest}
                        variant={isSingleLineBold ? 'subtitle1' : 'body1'}
                        gutterBottom={!isSingleLineBold}
                        // increase the margin-bottom for single-line bold markdown
                        style={isSingleLineBold ? { marginBottom: '1.25rem' } : {}}
                      />
                    )
                  },
                  h2: ({ node, ordered, ...rest }) => <Typography {...rest} component="h2" variant="h6" />,
                  ul: ({ node, ordered, ...rest }) => <ul {...rest} style={{ marginTop: '0.25em' }} />,
                  li: ({ node, ordered, ...rest }) => <li><Typography {...rest} /></li>,
                }}
              >
                {event.description}
              </ReactMarkdown>
              {/* eslint-enable react/prop-types */}
            </Box>
            <Box>
              {event.tags.length ? event.tags.filter(tag => (tag || '').trim().length > 0).map(tag => (
                <Chip key={tag} color="primary" icon={<CheckIcon />} label={tag} style={{ margin: '5px' }} />
              )) : null}
            </Box>
            <Divider className={classes.divider} />
            <Typography id="event-details" variant="h6">
              Event Details
            </Typography>
            <Box display="flex" alignItems="center">
              <EventNoteIcon style={{ marginRight: '16px' }} />
              <Box display="flex" flexDirection="column" style={{ margin: '10px' }}>
                <Typography className={classes.bodyText}>
                  Starts on {
                    fullDayDateAndTimeWithTimezone(
                      event.sessions[0]?.startDateTime,
                      event.sessions[0]?.endDateTime,
                      event.timezone,
                    )
                  }
                </Typography>
              </Box>
            </Box>
            {event.sessions.length > 1 && (
              <Box display="flex" alignItems="center">
                <EventNoteIcon style={{ marginRight: '16px', visibility: 'hidden' }} />
                <Box style={{ margin: '0 10px' }}>
                  <SessionsAccordion
                    sessions={event.sessions}
                    timezone={event.timezone}
                    isExpanded={sessionsExpanded}
                    setIsExpanded={setSessionsExpanded}
                  />
                </Box>
              </Box>
            )}
            {event.registrationCloseDateTime != null && !isPGACampEvent && (
              <Box display="flex" alignItems="center">
                <TodayIcon style={{ marginRight: '16px' }} />
                <Box display="flex" flexDirection="column" style={{ margin: '10px' }}>
                  <Typography className={classes.bodyText}>
                    Registration closes on {
                      getDate(event.registrationCloseDateTime)
                    } • {
                      getTime(event.registrationCloseDateTime)
                    }
                  </Typography>
                </Box>
              </Box>
            )}
            {isMobile && (
              <Box display="flex" alignItems="center">
                <CreditCardIcon style={{ marginRight: '16px' }} />
                <Box display="flex" flexDirection="column" style={{ margin: '10px' }}>
                  <Typography className={classes.bodyText}>
                    {formattedGroupEventPrice(event, false)}
                  </Typography>
                  {isRecurringPayment(event) && (
                    <Typography className={classes.captionText}>
                      {buildPaymentDurationLabel(event)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            {event.requiresAccessCode && (
              <Box display="flex" alignItems="center">
                <LockIcon style={{ marginRight: '16px' }} />
                <Box display="flex" flexDirection="column" style={{ margin: '10px' }}>
                  <Typography className={classes.bodyText}>
                    Requires Access Code
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <Box display={isMobile ? 'none' : 'block'} style={{ marginTop: '10px' }}>
            <Card className={classes.registerBox}>
              <CardContent>
                <Typography variant="h6" style={{ textAlign: 'center' }}>Book this event</Typography>
                <Divider style={{ margin: '15px 0' }} />
                <Typography className={classes.cardSubtitle}>Event date</Typography>
                <Typography className={classes.cardText}>
                  {event.sessions.length > 1 && 'Starts '}
                  {
                    getShortDate(
                      event.startDateTime,
                      event.timezone,
                    )
                  }
                </Typography>
                <Typography className={classes.cardText}>
                  {
                    getTimeWithTimeZone(
                      event.startDateTime,
                      event.endDateTime,
                      event.timezone,
                    )
                  }
                </Typography>
                {event.sessions.length > 1 && (
                  <Typography
                    className={`${classes.cardText} ${classes.link}`}
                    onClick={handleSessionsClick}
                  >
                    {event.sessions.length} sessions
                  </Typography>
                )}
                <Typography className={classes.cardSubtitle} style={{ marginTop: '24px' }}>Cost</Typography>
                <Typography className={classes.cardText}>
                  {formattedGroupEventPrice(event, false)}
                </Typography>
                {isRecurringPayment(event) && (
                  <Typography className={classes.cardSubtext}>
                    {buildPaymentDurationLabel(event)}
                  </Typography>
                )}
                {totalPriceInDollars && totalPriceInDollars !== priceInDollars && (
                  <Typography className={classes.cardSubtext}>
                    Total Cost: {totalPriceInDollarsFormatted}
                  </Typography>
                )}
                {event?.requiresAccessCode && (
                  <Box display="flex" flexDirection="column" textAlign="center" mt={2}>
                    <RequestAccessCode />
                  </Box>
                )}
                {showWaitlistDialog && (
                  <>
                    <Typography className={classes.cardSubtitle} style={{ marginTop: '20px' }}>
                      Registration
                    </Typography>
                    <Typography className={classes.cardText}>
                      {waitlistText}
                    </Typography>
                  </>
                )
                }
                <RegisterButton event={event} />
              </CardContent>
            </Card>
          </Box>
        </Box>

        <EventHostInfo event={event} />

        <Divider className={classes.divider} />
        <Typography variant="h6">Location</Typography>
        <Box display="flex" alignItems="center">
          <LocationOnIcon style={{ marginRight: '16px' }} />
          <Box display="flex" flexDirection="column" style={{ margin: '10px' }}>
            <Typography className={classes.bodyText}>{event.coachFacility.name}</Typography>
            {path(['coachFacility', 'city'], event) && path(['coachFacility', 'state'], event)
              ? (
                <Typography className={classes.captionText}>
                  {`${event.coachFacility.city}, ${event.coachFacility.state}`}
                </Typography>
              ) : null}
          </Box>
        </Box>
        {event?.academy?.slug && (
          <>
            <Divider className={classes.divider} />
            <Typography variant="h6" style={{ marginBottom: '8px' }}>
              {academyTitle}
            </Typography>
            <Box display="flex" alignItems="center">
              <LinkIcon style={{ marginRight: '16px' }} />
              <Link link={academyUrl()} className={classes.link}>
                {event?.academy?.name}
              </Link>
            </Box>
          </>
        )}
      </Container>
      <NoSsr>
        <EventMap coachFacility={event.coachFacility} />
      </NoSsr>
      <Container className={classes.root}>
        <NoSsr>
          <MoreEvents groupEventSlug={event.slug} />
        </NoSsr>
        <Hidden mdUp implementation="css">
          {event?.requiresAccessCode && (
            <Box className={classes.registerMobileSection} style={{ bottom: '80px' }}>
              <RequestAccessCode />
            </Box>
          )}
          <Box className={classes.registerMobileSection}>
            <Box display="flex" flexDirection="column" alignItems="flex-start">
              <Typography className={classes.cardText}>
                {formattedGroupEventPrice(event, false)}
              </Typography>
              {totalPriceInDollars && totalPriceInDollars !== priceInDollars && (
                <Typography variant="body2">
                  Total Cost: {totalPriceInDollarsFormatted}
                </Typography>
              )}
              {showWaitlistDialog && (
                <Typography className={classes.captionText}>{waitlistText}</Typography>
              )}
            </Box>
            <RegisterButton event={event} />
          </Box>
        </Hidden>
      </Container>
    </>
  )
}

GroupEventPage.propTypes = {
  event: PropTypes.shape({
    title: PropTypes.string,
    createdAt: PropTypes.string,
    featuredImageUrl: PropTypes.string,
    description: PropTypes.string,
    isCanceled: PropTypes.bool,
    unlisted: PropTypes.bool,
    tags: PropTypes.array,
    startDateTime: PropTypes.string,
    endDateTime: PropTypes.string,
    timezone: PropTypes.string,
    registrationCloseDateTime: PropTypes.string,
    priceInCents: PropTypes.number,
    totalPriceInCents: PropTypes.number,
    paymentRecurrenceSchedule: PropTypes.string,
    numberOfPayments: PropTypes.number,
    slug: PropTypes.string,
    requiresAccessCode: PropTypes.bool,
    registrationFull: PropTypes.bool,
    hasWaitlist: PropTypes.bool,
    maxAttendance: PropTypes.number,
    sessions: PropTypes.arrayOf(PropTypes.shape({
      startDateTime: PropTypes.string,
      endDateTime: PropTypes.string,
    })),
    academy: PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
    }),
    coachFacility: PropTypes.shape({
      name: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      street: PropTypes.string,
      zipCode: PropTypes.string,
      groupEvents: PropTypes.arrayOf(PropTypes.shape({
        startDateTime: PropTypes.string,
        endDateTime: PropTypes.string,
        title: PropTypes.string,
        featuredImageUrl: PropTypes.string,
        slug: PropTypes.string,
      })),
    }),
    coach: PropTypes.shape({
      name: PropTypes.string,
      profileUrl: PropTypes.string,
      coachProfile: PropTypes.shape({
        profilePhoto: PropTypes.string,
      }),
    }),
  }),
  facilityEvents: PropTypes.arrayOf(PropTypes.shape({
    endDateTime: PropTypes.string,
    startDateTime: PropTypes.string,
    slug: PropTypes.string,
    title: PropTypes.string,
    featuredImageUrl: PropTypes.string,
    coachFacility: PropTypes.shape({
      name: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      zipCode: PropTypes.string,
    }),
  })),
}

export default GroupEventPage
